import { useEffect, useState } from 'react';
import { Banner, BlockStack, Button, Checkbox, Modal, Text } from '@shopify/polaris';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../App';
import type { compileOrderAutomation } from '../../../packages/shared/automation';

type Review = {
  rule_id: number; name: string; enabled: boolean; saved_enabled: boolean; revision: number;
  definition_digest: string; problem: string | null; unavailable: string | null;
  review: ReturnType<typeof compileOrderAutomation> | null; jobs: Array<{ state: string; count: number }>;
};
type Command = { enabled: boolean; revision: number; definition_digest: string; expected_user_id: number; request_id: string };
export default function AutomationExecution({ ruleId, close, saved }: { ruleId: number; close(): void; saved(): void }) {
  const { fetchApi, loading, error } = useApi();
  const { user } = useAuth();
  const [view, setView] = useState<Review | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [pending, setPending] = useState<Command | null>(null);
  useEffect(() => {
    let current = true;
    setView(null); setConfirmed(false); setPending(null);
    void fetchApi<{ data: Review }>(`/automation/execution/${ruleId}`).then(reply => { if (current && reply) setView(reply.data); });
    return () => { current = false; };
  }, [fetchApi, ruleId]);
  const save = async () => {
    if (!view) return;
    const command = pending ?? { enabled: !view.enabled, revision: view.revision, definition_digest: view.definition_digest, expected_user_id: Number(user?.id), request_id: crypto.randomUUID() };
    setPending(command);
    const reply = await fetchApi<{ data: Review }>(`/automation/execution/${ruleId}`, { method: 'PUT', body: JSON.stringify(command) });
    if (reply) { setView(reply.data); setPending(null); setConfirmed(false); saved(); }
  };
  return <Modal open title="Review automatic execution" onClose={close} secondaryActions={[{ content: 'Close', onAction: close }]}>
    <Modal.Section><BlockStack gap="300">
      {error && <Banner tone="critical">{error} Reopen this review to check the saved state, or retry the same request below.</Banner>}
      {!view && <Text as="p">{loading ? 'Loading the current rule…' : 'The rule could not be loaded. Close this window and try again.'}</Text>}
      {view && <>
        <Text as="h3" variant="headingMd">{view.name}</Text>
        <Banner tone={view.enabled ? 'success' : 'info'}>{view.enabled ? 'Automatic execution is active for future events.' : 'This rule is not running automatically.'}</Banner>
        {view.problem && <Banner tone="warning">{view.problem}</Banner>}
        {view.unavailable && <Banner tone="warning">{view.unavailable}</Banner>}
        {!view.saved_enabled && <Text as="p">Enable the saved rule, then reopen this review to activate execution.</Text>}
        {view.review && <>
          <Text as="p">Event: {view.review.trigger_event.replace(/_/g, ' ')}</Text>
          <Text as="p">Conditions use current order values when the job runs. All conditions must match.</Text>
          {!view.review.conditions.length && <Text as="p">No conditions: every future matching event can run these actions.</Text>}
          {view.review.conditions.map((row, index) => <Text as="p" key={index}>{row.field}: {row.operator.replace(/_/g, ' ')} {String(row.value ?? '')}</Text>)}
          <Text as="h3" variant="headingSm">Actions within Whenly</Text>
          {view.review.actions.map((row, index) => <Text as="p" key={index}>{row.type.replace(/_/g, ' ')}: {row.value}</Text>)}
        </>}
        <Text as="p">Runs normally begin within a minute. Pausing or editing stops pending work. Earlier events are not replayed when you activate again. Changes apply to Whenly orders only.</Text>
        <Text as="p">History: {view.jobs.length ? view.jobs.map(row => `${row.count} ${row.state}`).join(', ') : 'No runs yet.'}</Text>
        {!view.enabled && !view.unavailable && view.saved_enabled && <Checkbox label="I have reviewed this rule and want these actions to run automatically for future events." checked={confirmed} onChange={setConfirmed} disabled={loading || !!pending} />}
        <Button variant="primary" loading={loading} disabled={!pending && !view.enabled && (!confirmed || !view.saved_enabled || !!view.unavailable)} onClick={() => void save()}>
          {pending ? 'Retry the same request' : view.enabled ? 'Pause execution' : 'Activate execution'}
        </Button>
      </>}
    </BlockStack></Modal.Section>
  </Modal>;
}
